import React, { useEffect } from 'react';
import Animated, {
  Easing,
  cancelAnimation,
  useAnimatedProps,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Circle } from 'react-native-svg';

import type { LoadMainVariantProps } from '../types';
import { LOAD_MAIN_EASING } from '../loadMainMotion';

const AnimatedCircle = Animated.createAnimatedComponent(Circle);

export function SpinnerLoader({ size, colors, duration_ms }: LoadMainVariantProps) {
  const stroke_width = Math.max(2.5, size * 0.075);
  const radius = (size - stroke_width) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  const rotation = useSharedValue(0);
  const sweep = useSharedValue(0.2);

  useEffect(() => {
    rotation.value = 0;
    sweep.value = 0.18;
    rotation.value = withRepeat(
      withTiming(360, { duration: duration_ms, easing: Easing.linear }),
      -1,
      false,
    );
    sweep.value = withRepeat(
      withSequence(
        withTiming(0.78, {
          duration: duration_ms * 0.42,
          easing: LOAD_MAIN_EASING.smooth,
        }),
        withTiming(0.18, {
          duration: duration_ms * 0.42,
          easing: LOAD_MAIN_EASING.soft,
        }),
        withTiming(0.18, { duration: duration_ms * 0.16 }),
      ),
      -1,
      false,
    );
    return () => {
      cancelAnimation(rotation);
      cancelAnimation(sweep);
    };
  }, [duration_ms, rotation, sweep]);

  const spin_style = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }));

  const arc_props = useAnimatedProps(() => {
    const arc_length = circumference * sweep.value;
    return {
      strokeDasharray: [Math.max(arc_length, 1), circumference],
    };
  });

  return (
    <Animated.View style={[{ width: size, height: size }, spin_style]}>
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={colors.track}
          strokeWidth={stroke_width}
          fill="none"
        />
        <AnimatedCircle
          cx={center}
          cy={center}
          r={radius}
          stroke={colors.primary}
          strokeWidth={stroke_width}
          fill="none"
          strokeLinecap="round"
          animatedProps={arc_props}
        />
      </Svg>
    </Animated.View>
  );
}
